import { useEffect, useState, type ReactNode } from 'react';
import { Anchor, Badge, Box, Divider, Group, Paper, Skeleton, Stack, Text, UnstyledButton } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { NewsItem, NewsTier } from '@swiftpay/shared';
import { newsApi } from '../../../api/news.js';

const TIER_META: Record<NewsTier, { label: string; color: string }> = {
  1: { label: 'РОССИЯ', color: '#e03131' },
  2: { label: 'ИНДУСТРИЯ', color: '#f59f00' },
  3: { label: 'МИР', color: '#1971c2' },
};

const LIMIT = 3;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function NewsRow({ item, compact }: { item: NewsItem; compact: boolean }) {
  const meta = item.tier ? TIER_META[item.tier] : null;
  const date = formatDate(item.postedAt);
  const footer = [date, item.source].filter(Boolean).join(' · ');
  return (
    <UnstyledButton component={Link} to="/news" px="md" py={compact ? 8 : 12} style={{ borderRadius: 12, display: 'block' }}>
      <Stack gap={6}>
        {meta && (
          <Box>
            <Badge variant="light" color={meta.color} size="sm" radius="sm">{meta.label}</Badge>
          </Box>
        )}
        <Text fw={600} fz={15} c="#14201B" lh={1.35} lineClamp={2}>{item.title}</Text>
        {/* compact: колонка новостей — самая высокая на главной; без саммари
            дашборд влезает в невысокий экран, полный текст остаётся на /news */}
        {item.summary && !compact && <Text fz={13} c="#6B7570" lh={1.5} lineClamp={2}>{item.summary}</Text>}
        {footer && <Text fz={12} c="#6B7570">{footer}</Text>}
      </Stack>
    </UnstyledButton>
  );
}

/**
 * Три свежие новости на главной клиента. Источник временно — парсинг-лента
 * табачного рынка (та же, что на /news); когда появятся «Новости компании»,
 * заменится только источник данных, вёрстка блока останется.
 */
export function LatestNews({ isDesktop, compact }: { isDesktop: boolean; compact: boolean }) {
  const [items, setItems] = useState<NewsItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    newsApi.list()
      .then((r) => {
        const sorted = [...r.items].sort((a, b) => b.postedAt.localeCompare(a.postedAt));
        setItems(sorted.slice(0, LIMIT));
      })
      .catch(() => setFailed(true)); // новости не пришли — блок просто не показываем
  }, []);

  const header = (
    <Group justify="space-between" wrap="nowrap" mb={compact ? 8 : 'sm'}>
      <Text fw={700} fz={compact ? 20 : 22} c="#14201B">Последние новости</Text>
      <Anchor component={Link} to="/news" c="#0C8C7A" fz={14}>Все новости</Anchor>
    </Group>
  );

  // Ошибка или пустая лента — блок не показываем совсем (главная не ломается).
  if (failed || (items !== null && items.length === 0)) return null;

  const shell = (children: ReactNode) => (
    <Paper
      radius={16}
      p={compact ? 'lg' : (isDesktop ? 'xl' : 'md')}
      style={{
        background: '#fff',
        boxShadow: '0 1px 4px rgb(20 32 27 / 6%)',
        flex: isDesktop ? 1 : undefined,
        minWidth: 0,
      }}
    >
      {header}
      {children}
    </Paper>
  );

  if (items === null) {
    return shell(
      <Stack gap="sm">
        <Skeleton h={64} radius={12} />
        <Skeleton h={64} radius={12} />
        <Skeleton h={64} radius={12} />
      </Stack>,
    );
  }

  return shell(
    <Stack gap={0}>
      {items.map((it, i) => (
        <Box key={it.id}>
          {i > 0 && <Divider color="#EFF1F0" />}
          <NewsRow item={it} compact={compact} />
        </Box>
      ))}
    </Stack>,
  );
}
